<template>
  <Layout>
    <StaleJobBanner v-if="job.isStale" :title="bannerTitle" :message="bannerMessage" />
    <DetailForm
      v-if="loaded"
      title="Edit"
      description="This form will edit a job. Jobs are publicly available for candidates to apply to and may be aggregated to job portals."
      :action="job.isStale && !job.draftJobDetails ? 'Refresh' : job.draftJobDetails ? 'Approve' : 'Save'"
      :show-secondary-action="$route.query.draft === 'true'"
      secondary-action="Reject"
      @handle-cancel="$router.go(-1)"
      @handle-action="handleSubmit"
      @handle-secondary-action="handleReject"
    >
      <TextInput
        class="mb-12"
        label="Title"
        placeholder="Real Estate Agent"
        :model-value="form.title"
        @update:model-value="value => (form.title = value)"
      />
      <CitiesInput
        v-model="form.locations"
        label="Job Locations"
        :error="locationError"
        @update:model-value="handleLocationsUpdate"
      />
      <ToggleInput
        v-model="form.resume_required"
        label="Resume Required"
        description="Require applicants to submit a resume when applying for this position"
      />
      <template #footer>
        <QuillInput
          v-model="form.description"
          :editor-toolbar="customToolbar"
          label="Description"
          name="job-description"
          size="x-large"
        />
      </template>
    </DetailForm>
  </Layout>
</template>

<script>
import { mapState } from 'vuex';
import Layout from '../Layout/index.vue';
import DetailForm from '../shared/DetailForm.vue';
import StaleJobBanner from './StaleJobBanner.vue';
import QuillInput from '../shared/QuillInput.vue';
import ToggleInput from '../shared/ToggleInput.vue';
import CitiesInput from '../shared/CitiesInput.vue';
import { createJobLocation, deactivateJobLocation } from '../../api/jobs.ts';

export default {
  components: {
    Layout,
    DetailForm,
    StaleJobBanner,
    QuillInput,
    ToggleInput,
    CitiesInput,
  },
  props: ['record'],
  data() {
    return {
      form: {
        title: '',
        description: '',
        resume_required: false,
        locations: [],
      },
      initialDescription: '',
      showEdit: false,
      selectedTab: 'Humaniz Description',
      customToolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
      loaded: false,
      originalCities: [],
      locationError: '',
    };
  },
  computed: {
    ...mapState('job', {
      job: state => state.currentJob,
    }),
    bannerTitle() {
      return this.$route.query.draft === 'true' ? 'Please have a look at this AI generated job refresh' : undefined;
    },
    bannerMessage() {
      return this.$route.query.draft === 'true'
        ? "We've used AI to generate a refreshed version of your job posting. Please review the changes and make any necessary adjustments before publishing."
        : undefined;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue?.params?.id != oldValue?.params?.id) {
          this.$store.dispatch('job/flushCurrentJob');
        }
      },
    },
  },
  async mounted() {
    await this.getJob();
  },
  methods: {
    async getJob() {
      await this.$store.dispatch('job/loadCurrentJob', {
        id: this.$route.params.id,
        draft: this.$route.query.draft === 'true',
      });

      this.form.title = this.job.title;
      const description =
        this.$route.query.draft === 'true' ? this.job.draftJobDetails.description : this.job.description;
      this.form.description = description;
      this.form.resume_required = this.job.resumeRequired || false;

      // Ensure we have valid location data
      const cities = this.job.cities || [];
      this.form.locations = cities
        .map(location => ({
          id: location.id,
          city: location.city || '',
          state: location.state || '',
          country: location.country || 'USA',
          latitude: location.latitude || 0,
          longitude: location.longitude || 0,
        }))
        .filter(loc => loc.city && loc.state);

      this.originalCities = [...this.form.locations];
      this.initialDescription = description;
      this.showEdit = true;
      this.loaded = true;
    },

    handleLocationsUpdate(locations) {
      this.form.locations = locations;
      this.validateLocations();
    },

    validateLocations() {
      if (!this.form.locations.length) {
        this.locationError = 'Please select at least one location for the job.';
        return false;
      }
      this.locationError = '';
      return true;
    },

    async handleSubmit() {
      try {
        if (!this.validateForm()) {
          return;
        }

        await this.updateJob();
        await this.handleSuccess();
      } catch (error) {
        this.handleError(error);
      }
    },

    validateForm() {
      if (!this.form.title) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Title Required',
          alertDescription: 'Please enter a job title.',
        });
        return false;
      }

      if (!this.validateLocations()) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Location Required',
          alertDescription: this.locationError,
        });
        return false;
      }

      return true;
    },

    async updateJob() {
      try {
        await this.syncLocations();

        const updatedJobAttributes = {
          title: this.form.title,
          resume_required: this.form.resume_required,
          job_details: {
            description: this.form.description,
            status: 'active',
          },
        };

        if (this.$route.query.draft === 'true') {
          updatedJobAttributes.draft_to_active = true;
        }

        return await this.$store.dispatch('job/updateJob', {
          id: this.$route.params.id,
          updatedJobAttributes,
        });
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    },

    async syncLocations() {
      const createLocationKey = loc =>
        loc.id ? `id-${loc.id}` : `${loc.city.toLowerCase()}-${loc.state.toLowerCase()}`;
      const formKeys = new Set(this.form.locations.map(createLocationKey));
      const originalKeys = new Set(this.originalCities.map(createLocationKey));

      const addedLocations = this.form.locations.filter(loc => !originalKeys.has(createLocationKey(loc)));
      const removedLocations = this.originalCities.filter(orig => !formKeys.has(createLocationKey(orig)));

      if (addedLocations.length > 0 || removedLocations.length > 0) {
        if (addedLocations.length > 0) {
          await Promise.all(
            addedLocations.map(loc => {
              const payload = {
                ...loc,
                status: 'active',
                latitude: String(loc.latitude),
                longitude: String(loc.longitude),
              };
              return createJobLocation({ id: this.$route.params.id }, { newJobLocationAttributes: payload });
            })
          );
        }

        if (removedLocations.length > 0) {
          await Promise.all(
            removedLocations
              .filter(loc => loc.id)
              .map(loc => {
                return deactivateJobLocation({
                  jobId: this.$route.params.id,
                  locationId: loc.id.toString(),
                });
              })
          );
        }
      }
    },

    async handleSuccess() {
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'Job edited',
        alertDescription: 'Your job was successfully edited. Use the button below to dismiss this alert.',
      });

      await this.$router.push({
        name: 'JobDetail',
        params: { id: this.$route.params.id },
      });
    },

    handleError(error) {
      this.$store.commit('general/showAlert', {
        alertType: 'error',
        alertTitle: 'Error editing job',
        alertDescription: error.message || 'An error occurred while editing the job.',
      });
    },

    async handleReject() {
      try {
        await this.$store.dispatch('job/updateJob', {
          id: this.$route.params.id,
          updatedJobAttributes: {
            reject_draft: true,
            job_details: { description: this.initialDescription },
          },
        });

        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Draft rejected',
          alertDescription: 'The draft changes have been rejected.',
        });

        await this.$router.push({
          name: 'JobDetail',
          params: { id: this.$route.params.id },
        });
      } catch (error) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Error rejecting draft',
          alertDescription: error.message || 'An error occurred while rejecting the draft.',
        });
      }
    },
  },
};
</script>
